import type { BadgeTone } from '@/components/ui/primitives'
import type { CapabilityGatedKind } from '@/lib/comments'

/**
 * The five kinds of record, and the discussion written on them. The item kinds are the
 * labels comments carry, so they are the same list by construction rather than by
 * coincidence — see `lib/comments.ts`.
 */
// Not messages: a thread is readable by its participants rather than by a role, and the
// search is one collection-group query gated per kind — it has no way to ask who is on a
// conversation. Threads are found in the Messages tab, which reads only your own.
export type ItemKind = CapabilityGatedKind
export type SearchKind = ItemKind | 'comment'

/**
 * One searchable thing, flattened.
 *
 * `title` and `subtitle` are read on screen. `body` is everything else worth matching and
 * is never shown — the answer somebody wrote, the notes, the API name, the second SKU.
 * Both are already markdown-stripped: the descriptive fields are markdown now, and nobody
 * searches for the asterisks.
 */
export type SearchRecord = {
  /** `${kind}:${targetId}`. Unique across the kinds, and the row's value in the list. */
  key: string
  kind: SearchKind
  /** What the URL parameter for this kind carries — the id its page opens on. */
  targetId: string
  /**
   * For a comment, the kind of item it was written on. A comment has nowhere of its own
   * to open at, so it opens the discussion it belongs to.
   */
  parentKind?: ItemKind
  title: string
  subtitle: string
  body: string
  /** The one-word state shown on the right of the row. Empty when the kind has none. */
  status: string
  tone: BadgeTone
}

/** Plural, and the same words the tabs use, because that is where a result sends you. */
export const KIND_LABELS: Record<SearchKind, string> = {
  decision: 'Decisions',
  task: 'Roadmap',
  field: 'Field Guide',
  product: 'Products',
  owner: 'Owners',
  // The same words the two org chart sub-tabs use, because that is where a result sends you.
  staff: 'Staff Roster',
  orgContainer: 'Org Chart',
  // A note is one of the diagram's side panels, opened from the same tab as a container.
  orgNote: 'Org Chart',
  // A sharing rule is a row on the chart's third sub-tab, opened from the same tab again.
  orgSharingRule: 'Org Chart',
  comment: 'Comments',
}

/** Results are grouped in this order regardless of which kind scored best. */
export const KIND_ORDER: SearchKind[] = [
  'decision', 'task', 'owner', 'staff', 'orgContainer', 'orgNote', 'orgSharingRule',
  'field', 'product', 'comment',
]

/** The kinds that are records in their own right, in the order the tabs sit in. */
export const ITEM_KINDS: ItemKind[] = ['decision', 'task', 'owner', 'field', 'product']

/**
 * The capability a kind needs, so the index never holds what the rules would refuse.
 * `firestore.rules` maps the same set, once for the item and once for its comments.
 *
 * `staff`, `orgContainer`, `orgNote` and `orgSharingRule` are mapped here so this object
 * stays complete over `ItemKind` — `useSearchRecords.ts` indexes it with the full union —
 * but none of the four is in `ITEM_KINDS` above: indexing people, containers, notes and
 * sharing rules for search is a later task's job, not this one's.
 */
export const KIND_CAPABILITY = {
  decision: 'decisions.view',
  task: 'roadmap.view',
  field: 'fields.view',
  product: 'products.view',
  owner: 'owners.view',
  // Each of the four carries the grant `kindCapability()` in `firestore.rules` gives it —
  // the roster and the sharing rules table are their own readerships, not the chart's.
  staff: 'staff.view',
  orgContainer: 'orgchart.view',
  orgNote: 'orgchart.view',
  orgSharingRule: 'sharingrule.view',
} as const
